import React, { useState, useEffect } from "react";
import { Calendar, Check, X, AlertCircle, CheckCircle2, XCircle, Clock, ChevronDown } from "lucide-react";
import { POScheduledApprovalStatus, ISOString } from "src/types";
import { DD_MM_YYYY } from "src/helpers";
import { Select, SelectItem } from "src/shadcn/ui/select";

export interface EditScheduledPOApprovalSavePayload {
  scheduledDate?: string;
  approvalStatus?: "Pending" | "Approved" | "Rejected";
}

export interface PurchaseOrderScheduledEditBlockProps {
  scheduledDate: ISOString;
  scheduledPOApprovalStatus?: POScheduledApprovalStatus;
  isBackorder: boolean;
  onSaveScheduledEdit?: (payload: EditScheduledPOApprovalSavePayload) => void;
  isSavingScheduledEdit?: boolean;
  scheduledEditError?: Error | null;
  isEditing?: boolean;
  onEditingChange?: (value: boolean) => void;
}

const APPROVAL_STATUS_OPTIONS: { value: POScheduledApprovalStatus; label: string }[] = [
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approve" },
  { value: "Rejected", label: "Reject" }
];

const getApprovalStatusConfig = (status?: POScheduledApprovalStatus) => {
  switch (status) {
    case "Pending":
      return { label: "Pending", badgeClass: "border-amber-400/40 bg-amber-500/20 text-amber-100", icon: Clock };
    case "Approved":
      return {
        label: "Approved",
        badgeClass: "border-emerald-400/40 bg-emerald-500/20 text-emerald-100",
        icon: CheckCircle2
      };
    case "Rejected":
      return { label: "Rejected", badgeClass: "border-red-400/40 bg-red-500/20 text-red-100", icon: XCircle };
    default:
      return null;
  }
};

const PurchaseOrderScheduledEditBlock: React.FC<PurchaseOrderScheduledEditBlockProps> = ({
  scheduledDate,
  scheduledPOApprovalStatus,
  isBackorder,
  onSaveScheduledEdit,
  isSavingScheduledEdit = false,
  scheduledEditError = null,
  isEditing: isEditingProp,
  onEditingChange
}) => {
  const canEdit = onSaveScheduledEdit != null;
  const [isEditingInternal, setIsEditingInternal] = useState(false);
  const isControlled = onEditingChange != null;
  const isEditing = canEdit && (isControlled ? (isEditingProp ?? false) : isEditingInternal);

  const [localScheduledDate, setLocalScheduledDate] = useState(scheduledDate ?? "");
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState<POScheduledApprovalStatus | "">("");

  useEffect(() => {
    setLocalScheduledDate(scheduledDate ?? "");
  }, [scheduledDate]);

  useEffect(() => {
    if (isEditing) setSelectedApprovalStatus(scheduledPOApprovalStatus ?? "");
  }, [isEditing, scheduledPOApprovalStatus]);

  const setEditing = (value: boolean) => {
    if (isControlled) onEditingChange?.(value);
    else setIsEditingInternal(value);
  };

  const canEditDate = isBackorder;
  const statusConfig = getApprovalStatusConfig(scheduledPOApprovalStatus);
  const editModeStatus = (selectedApprovalStatus || scheduledPOApprovalStatus) as POScheduledApprovalStatus | undefined;
  const editModeStatusConfig = getApprovalStatusConfig(editModeStatus);

  const hasDateChange = canEditDate && localScheduledDate !== (scheduledDate ?? "") && localScheduledDate.length > 0;
  const hasApprovalChange =
    selectedApprovalStatus !== "" && selectedApprovalStatus !== (scheduledPOApprovalStatus ?? "");
  const hasChanges = hasDateChange || hasApprovalChange;

  const handleSave = () => {
    if (!onSaveScheduledEdit || !hasChanges || isSavingScheduledEdit) return;
    const payload: EditScheduledPOApprovalSavePayload = {};
    if (hasDateChange && localScheduledDate) payload.scheduledDate = localScheduledDate;
    if (hasApprovalChange && selectedApprovalStatus) payload.approvalStatus = selectedApprovalStatus;
    if (Object.keys(payload).length > 0) {
      onSaveScheduledEdit(payload);
    }
    if (!isControlled) setEditing(false);
  };

  const handleCancel = () => {
    setLocalScheduledDate(scheduledDate ?? "");
    setSelectedApprovalStatus("");
    setEditing(false);
  };

  const today = new Date();
  const minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  return (
    <div className="flex flex-col items-end gap-2">
      {/* Row 1: Schedule + Status */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Schedule: static date or date picker (only when editing) */}
        <div className="flex items-center gap-2 rounded-lg bg-amber-500/20 px-3 py-1.5 border border-amber-400/30">
          {isEditing && canEditDate ? (
            <>
              <Calendar className="h-4 w-4 text-amber-100 flex-shrink-0" />
              <input
                type="date"
                value={localScheduledDate}
                onChange={(e) => setLocalScheduledDate(e.target.value)}
                min={minDate}
                disabled={isSavingScheduledEdit}
                className="bg-transparent border-0 text-sm font-medium text-amber-50 focus:outline-none focus:ring-0 disabled:opacity-70 [color-scheme:dark]"
                title="Scheduled date"
              />
            </>
          ) : (
            <>
              <Calendar className="h-4 w-4 text-amber-100 flex-shrink-0" />
              <span className="text-sm font-medium text-amber-50">Scheduled: </span>
              <span className="text-sm font-medium text-amber-50">
                {DD_MM_YYYY.formatDate(new Date(scheduledDate))}
              </span>
            </>
          )}
        </div>

        {/* Approval status: badge (default) or select (when editing) */}
        {isEditing ? (
          <div
            className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium min-w-[140px] transition-all duration-200 cursor-pointer focus-within:ring-2 focus-within:ring-white/30 focus-within:ring-offset-2 focus-within:ring-offset-secondary-800 ${
              editModeStatusConfig?.badgeClass
                ? `${editModeStatusConfig.badgeClass} hover:brightness-110 hover:border-opacity-70`
                : "border-amber-400/40 bg-amber-500/20 text-amber-50 hover:bg-amber-500/30 hover:border-amber-400/50"
            }`}
          >
            {editModeStatusConfig?.icon ? (
              <editModeStatusConfig.icon className="h-3.5 w-3.5 flex-shrink-0" />
            ) : (
              <Clock className="h-3.5 w-3.5 flex-shrink-0" />
            )}
            <div className="relative flex-1 min-w-0">
              <Select
                id="scheduled-po-approval-heading"
                value={selectedApprovalStatus}
                onChange={(e) => setSelectedApprovalStatus((e.target.value || "") as POScheduledApprovalStatus | "")}
                disabled={isSavingScheduledEdit}
                className="h-auto min-h-0 w-full rounded-md border-0 bg-transparent py-0.5 pr-6 text-xs text-inherit font-medium shadow-none focus:ring-0 focus:ring-offset-0 disabled:opacity-70 disabled:cursor-not-allowed [&>option]:bg-secondary-800 [&>option]:text-white [color-scheme:dark]"
                aria-label="Approval status"
              >
                {APPROVAL_STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </Select>
              <ChevronDown
                className="absolute right-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none opacity-80 text-inherit"
                aria-hidden
              />
            </div>
          </div>
        ) : (
          statusConfig && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium ${statusConfig.badgeClass}`}
            >
              <statusConfig.icon className="h-3.5 w-3.5" />
              {statusConfig.label}
            </span>
          )
        )}
      </div>

      {/* Row 2: Edit (default) or Save + Cancel (when editing) - only when editable */}
      {canEdit && (
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSavingScheduledEdit || !hasChanges}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/30 bg-white/10 px-2.5 py-1 text-xs font-medium text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSavingScheduledEdit ? (
                  <>
                    <div className="h-3.5 w-3.5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-3.5 w-3.5" />
                    Save
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSavingScheduledEdit}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/30 bg-white/10 px-2.5 py-1 text-xs font-medium text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/30 bg-white/10 px-2.5 py-1 text-xs font-medium text-white hover:bg-white/20 transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      )}

      {canEdit && scheduledEditError && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-red-500/20 border border-red-400/30 w-full sm:w-auto">
          <AlertCircle className="w-4 h-4 text-red-100 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-100">{scheduledEditError.message || "Failed to save."}</p>
        </div>
      )}
    </div>
  );
};

export { PurchaseOrderScheduledEditBlock };
